import { FC, useEffect, useState } from "react";
import axios from "axios";

import SectionHeader from "../Layout/SectionHeader";
import InlineAppLoading from "../UI/InlineAppLoading";
import GallerySlot from "./GallerySlot";
import NFT from "./NFT";

import classes from "./Gallery.module.css";
import { useDispatch, useSelector } from "react-redux";
import { setUserData, userState } from "../../store/user";
import GlobalAppLoading from "../UI/GlobalAppLoading";
import { setArtwork } from "../../store/artwork";
import { useNavigate } from "react-router-dom";
import InlineLoading from "../UI/InlineLoading";
import CountUp from "react-countup";
import Countdown from "react-countdown";
import Button from "../UI/Button";
import { getLang, i18n } from "../../i18n";

export interface ArtworkModelDocument {
  _id: string;
  userID: string;
  slot: string;
  imageAmazonS3URL: string;
  imageAmazonS3ThumbURL: string;
  title: string;
  profit: number;
  silentVotesCount: number;
  silentVotesWinner: number;
  battleVotesCount: number;
  battleVotesWinner: number;
  level: number;
  value: number;
  description: string;
  aiMentorReview: number;
  aiMentorDescription: string;
  category: string;
  tags: string[];
  author?: string;
}

interface Slot {
  unlocked: boolean;
  title: string;
  description?: string;
  unlockSlotPrice?: number;
  art: ArtworkModelDocument | null;
  progressTitle?: string;
  progressCurrent?: number;
  progressTotal?: number;
  unoccupied?: boolean;
}

interface APISlotResponse {
  key:
    | "freeSlot"
    | "votesSlot"
    | "battlesSlot"
    | "nftsSlot"
    | "invitesSlot"
    | "paidSlot";
  data: Slot;
}

interface Props {
  setPopupImage: React.Dispatch<React.SetStateAction<string>>;
}

const Gallery: FC<Props> = ({ setPopupImage }) => {
  const lang = getLang();
  const t = i18n.gallery[lang];

  const API_URL = process.env.REACT_APP_API_URL;
  const dispatch = useDispatch();
  const user = useSelector(userState);
  const [timeLeft, setTimeLeft] = useState(28800000);
  const [copied, setCopied] = useState(false);
  const tickHandler = (time: number) => {
    setTimeLeft(time);
  };
  const copyClickHandler = () => {
    navigator.clipboard.writeText(
      `https://app.raterart.com/?code=${user.inviteCode}`
    );
    setCopied(true);
  };
  const getFreeTokensHandler = async () => {
    setTimeLeft(28800000);
    await axios
      .patch(
        `${API_URL}/users/freeTokens`,
        {},
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${user.sessionToken}` },
        }
      )
      .then((response) => {
        dispatch(setUserData(response.data.user));
        if (response.data.user.freeARTCtakenLeft) {
          setTimeLeft(response.data.user.freeARTCtakenLeft);
        } else {
          setTimeLeft(0);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };
  const [nfts, setNfts] = useState<ArtworkModelDocument[] | []>([]);
  const [freeSlot, setFreeSlot] = useState<Slot | null>(null);
  const [votesSlot, setVotesSlot] = useState<Slot | null>(null);
  const [battlesSlot, setBattlesSlot] = useState<Slot | null>(null);
  const [nftsSlot, setNftsSlot] = useState<Slot | null>(null);
  const [invitesSlot, setInvitesSlot] = useState<Slot | null>(null);
  const [paidSlot, setPaidSlot] = useState<Slot | null>(null);
  const [screenIsLoading, setScreenIsLoading] = useState(true);
  const [isFileLoading, setIsFileLoading] = useState<boolean>(false);
  //const [collection, setCollection] = useState<ArtworkModelDocument[] | []>([]);
  const navigate = useNavigate();
  const preUploadImage = async (
    e: React.ChangeEvent<HTMLInputElement>,
    slot: "free" | "votes" | "battles" | "nfts" | "invites" | "paid"
  ) => {
    if (e.target.files) {
      setIsFileLoading(true);
      const formData = new FormData();
      formData.append("artwork", e.target.files[0]);
      axios
        .post(`${API_URL}/artworks`, formData, {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${user.sessionToken}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 60000,
        })
        .then((response) => {
          dispatch(setArtwork({ slot: slot, ...response.data.artwork }));
          setIsFileLoading(false);
          navigate("/upload");
        })
        .catch((err) => {
          console.log(err);
          setIsFileLoading(false);
        });
    }
  };

  // let collectionWinRate: number = 0;

  // function computeArtworkScore(a: {
  //   battleVotesCount: number;
  //   battleVotesWinner: number;
  //   silentVotesCount: number;
  //   silentVotesWinner: number;
  //   level: number;
  //   profit: number;
  // }) {
  //   // 1. Base winrate
  //   const totalVotes = (a.battleVotesCount || 0) + (a.silentVotesCount || 0);
  //   const totalWins = a.battleVotesWinner + a.silentVotesWinner;

  //   const baseWinRate = totalVotes > 0 ? totalWins / totalVotes : 0;
  //   const winrateFactor = Math.pow(baseWinRate, 1.4);

  //   // 2. Level factor
  //   const level = Math.max(1, Math.min(a.level, 8));
  //   const levelFactor = 0.5 + (level - 1) * 0.25;

  //   // 3. Profit factor
  //   const profit = a.profit;
  //   const normProfit = (profit + 250) / (500 + 250);
  //   const profitFactor = Math.pow(normProfit, 1.6) * 7.5;
  //   const profitFactorClamped = Math.min(Math.max(profitFactor, 0), 7.5);

  //   // 4. Confidence
  //   // 1. Non-linear saturation curve
  //   let confidence = 1 - Math.exp(-0.1386 * totalVotes);
  //   // 2. Clamp 0.1 - 1.0
  //   confidence = Math.min(Math.max(confidence, 0.1), 1);

  //   // 5. Combine factors
  //   let score = winrateFactor + levelFactor * 0.75 + profitFactorClamped * 0.25;
  //   score *= confidence;

  //   return score * 100; // 0-100 scale
  // }

  // if (collection.length) {
  //   collection.forEach((i) => {
  //     const itemScore = computeArtworkScore(i);
  //     // const battleRate =
  //     //   i.battleVotesCount > 0
  //     //     ? i.battleVotesWinner / i.battleVotesCount
  //     //     : null;
  //     // const silentRate =
  //     //   i.silentVotesCount > 0
  //     //     ? i.silentVotesWinner / i.silentVotesCount
  //     //     : null;

  //     // // base normalized weights (battle 4x silent -> 0.8 / 0.2)
  //     // const baseBattleWeight = 0.8;
  //     // const baseSilentWeight = 0.2;

  //     // let itemScore = 0;

  //     // if (battleRate !== null && silentRate !== null) {
  //     //   // both exist -> apply normalized weights
  //     //   itemScore =
  //     //     battleRate * baseBattleWeight + silentRate * baseSilentWeight;
  //     // } else if (battleRate !== null) {
  //     //   // only battle votes exist -> use battle only
  //     //   itemScore = battleRate;
  //     // } else if (silentRate !== null) {
  //     //   // only silent votes exist -> use silent only
  //     //   itemScore = silentRate;
  //     // } else {
  //     //   // no votes -> 0 (or skip depending on desired behavior)
  //     //   itemScore = 0;
  //     // }

  //     // // 2. Level multiplier (1–8)
  //     // const MAX_LEVEL = 8;
  //     // const levelMultiplier = 1 + (Math.min(i.level, MAX_LEVEL) - 1) * 0.1;
  //     // itemScore *= levelMultiplier;

  //     // const profitBoost = i.profit > 0 ? 1 + Math.min(i.profit / 50, 2) : 1;
  //     // itemScore *= profitBoost;

  //     // const confidence =
  //     //   1 /
  //     //   (1 + Math.exp(-0.2 * (i.battleVotesCount + i.silentVotesCount - 10)));
  //     // // 10 votes → ~0.5, 30 votes → ~0.95, 50 votes → ~0.99
  //     // itemScore *= confidence;

  //     collectionWinRate += itemScore;
  //   });

  //   // average per-item score
  //   collectionWinRate = collectionWinRate / collection.length;
  // } else {
  //   collectionWinRate = 0;
  // }
  // collectionWinRate = Number(collectionWinRate.toFixed(2));

  let userTotalGalleryValue = 0;
  if (freeSlot && freeSlot.art) {
    userTotalGalleryValue += freeSlot.art.value;
  }
  if (paidSlot && paidSlot.art) {
    userTotalGalleryValue += paidSlot.art.value;
  }
  if (votesSlot && votesSlot.art) {
    userTotalGalleryValue += votesSlot.art.value;
  }
  if (battlesSlot && battlesSlot.art) {
    userTotalGalleryValue += battlesSlot.art.value;
  }
  if (nftsSlot && nftsSlot.art) {
    userTotalGalleryValue += nftsSlot.art.value;
  }
  if (invitesSlot && invitesSlot.art) {
    userTotalGalleryValue += invitesSlot.art.value;
  }

  useEffect(() => {
    console.log("gallery use effect");
    const getUserGallery = async () => {
      setScreenIsLoading(true);
      await axios
        .get(`${API_URL}/users/gallery`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${user.sessionToken}` },
        })
        .then((response) => {
          setNfts(response.data.nfts);
          setFreeSlot(
            response.data.slots.filter(
              (slot: APISlotResponse) => slot.key === "freeSlot"
            )[0].data
          );
          setVotesSlot(
            response.data.slots.filter(
              (slot: APISlotResponse) => slot.key === "votesSlot"
            )[0].data
          );
          setBattlesSlot(
            response.data.slots.filter(
              (slot: APISlotResponse) => slot.key === "battlesSlot"
            )[0].data
          );
          setNftsSlot(
            response.data.slots.filter(
              (slot: APISlotResponse) => slot.key === "nftsSlot"
            )[0].data
          );
          setInvitesSlot(
            response.data.slots.filter(
              (slot: APISlotResponse) => slot.key === "invitesSlot"
            )[0].data
          );
          setPaidSlot(
            response.data.slots.filter(
              (slot: APISlotResponse) => slot.key === "paidSlot"
            )[0].data
          );
          //setCollection(response.data.artworks);
          setScreenIsLoading(false);
        })
        .catch((error) => {
          console.log(error);
          setScreenIsLoading(false);
        });
    };

    getUserGallery();
  }, []);
  if (isFileLoading) return <GlobalAppLoading text={t.reviewingPhoto} />;
  return (
    <>
      <SectionHeader label={t.galleryHeader} />
      <section>
        <div className="container">
          <div className={classes.topflex}>
            <div>
              <div className={classes.bg}>💰</div>
              <h2 style={{ textAlign: "center" }}>{t.balance}</h2>
              {screenIsLoading && (
                <div className={classes.loadingTokens}>
                  <InlineLoading />
                </div>
              )}
              {!screenIsLoading &&
                user &&
                user.totalARTC !== undefined &&
                user.totalARTC > 0 && (
                  <CountUp
                    className={`${classes.count}`}
                    duration={1}
                    start={0.0}
                    end={user.totalARTC}
                    separator=" "
                    decimals={2}
                    decimal="."
                  />
                )}
            </div>
            <div>
              <div className={classes.bg}>💎</div>
              <h2 style={{ textAlign: "center" }}>{t.galleryValue}</h2>
              {screenIsLoading && (
                <div className={classes.loadingTokens}>
                  <InlineLoading />
                </div>
              )}
              {!screenIsLoading &&
                user &&
                userTotalGalleryValue !== undefined &&
                userTotalGalleryValue > 0 && (
                  <CountUp
                    className={`${classes.count}`}
                    duration={1}
                    start={0.0}
                    end={Math.round(userTotalGalleryValue)}
                    separator=" "
                    decimals={0}
                    decimal="."
                  />
                )}
            </div>
          </div>

          <h2>{t.yourGallerySlots}</h2>
          <div className={classes.slots}>
            <GallerySlot
              slot="free"
              loading={screenIsLoading}
              title={freeSlot ? freeSlot.title : ""}
              description={
                freeSlot && freeSlot.description ? freeSlot.description : ""
              }
              unlocked={freeSlot ? freeSlot.unlocked : false}
              level={freeSlot && freeSlot.art ? freeSlot.art.level : 0}
              value={freeSlot && freeSlot.art ? freeSlot.art.value : 0}
              image={
                freeSlot && freeSlot.art ? freeSlot.art.imageAmazonS3URL : ""
              }
              price={freeSlot ? freeSlot.unlockSlotPrice : 1000}
              uploadFile={preUploadImage}
              profit={freeSlot && freeSlot.art ? freeSlot.art.profit : 0}
              unoccupied={freeSlot ? freeSlot.unoccupied : false}
            />
            <GallerySlot
              slot="paid"
              loading={screenIsLoading}
              title={paidSlot ? paidSlot.title : ""}
              description={
                paidSlot && paidSlot.description ? paidSlot.description : ""
              }
              unlocked={paidSlot ? paidSlot.unlocked : false}
              level={paidSlot && paidSlot.art ? paidSlot.art.level : 0}
              value={paidSlot && paidSlot.art ? paidSlot.art.value : 0}
              image={
                paidSlot && paidSlot.art ? paidSlot.art.imageAmazonS3URL : ""
              }
              price={paidSlot ? paidSlot.unlockSlotPrice : 1000}
              uploadFile={preUploadImage}
              profit={paidSlot && paidSlot.art ? paidSlot.art.profit : 0}
              unoccupied={paidSlot ? paidSlot.unoccupied : false}
            />
            <GallerySlot
              slot="votes"
              loading={screenIsLoading}
              title={votesSlot ? votesSlot.title : ""}
              description={
                votesSlot && votesSlot.description ? votesSlot.description : ""
              }
              unlocked={votesSlot ? votesSlot.unlocked : false}
              level={votesSlot && votesSlot.art ? votesSlot.art.level : 0}
              value={votesSlot && votesSlot.art ? votesSlot.art.value : 0}
              image={
                votesSlot && votesSlot.art ? votesSlot.art.imageAmazonS3URL : ""
              }
              price={votesSlot ? votesSlot.unlockSlotPrice : 1000}
              taskTitle={votesSlot ? votesSlot.progressTitle : ""}
              current={votesSlot ? votesSlot.progressCurrent : 0}
              total={votesSlot ? votesSlot.progressTotal : 100}
              uploadFile={preUploadImage}
              profit={votesSlot && votesSlot.art ? votesSlot.art.profit : 0}
              unoccupied={votesSlot ? votesSlot.unoccupied : false}
            />
            <GallerySlot
              slot="battles"
              loading={screenIsLoading}
              title={battlesSlot ? battlesSlot.title : ""}
              description={
                battlesSlot && battlesSlot.description
                  ? battlesSlot.description
                  : ""
              }
              unlocked={battlesSlot ? battlesSlot.unlocked : false}
              level={battlesSlot && battlesSlot.art ? battlesSlot.art.level : 0}
              value={battlesSlot && battlesSlot.art ? battlesSlot.art.value : 0}
              image={
                battlesSlot && battlesSlot.art
                  ? battlesSlot.art.imageAmazonS3URL
                  : ""
              }
              price={battlesSlot ? battlesSlot.unlockSlotPrice : 1000}
              taskTitle={battlesSlot ? battlesSlot.progressTitle : ""}
              current={battlesSlot ? battlesSlot.progressCurrent : 0}
              total={battlesSlot ? battlesSlot.progressTotal : 50}
              uploadFile={preUploadImage}
              profit={
                battlesSlot && battlesSlot.art ? battlesSlot.art.profit : 0
              }
              unoccupied={battlesSlot ? battlesSlot.unoccupied : false}
            />
            <GallerySlot
              slot="nfts"
              loading={screenIsLoading}
              title={nftsSlot ? nftsSlot.title : ""}
              description={
                nftsSlot && nftsSlot.description ? nftsSlot.description : ""
              }
              unlocked={nftsSlot ? nftsSlot.unlocked : false}
              level={nftsSlot && nftsSlot.art ? nftsSlot.art.level : 0}
              value={nftsSlot && nftsSlot.art ? nftsSlot.art.value : 0}
              image={
                nftsSlot && nftsSlot.art ? nftsSlot.art.imageAmazonS3URL : ""
              }
              price={nftsSlot ? nftsSlot.unlockSlotPrice : 1000}
              taskTitle={nftsSlot ? nftsSlot.progressTitle : ""}
              current={nftsSlot ? nftsSlot.progressCurrent : 0}
              total={nftsSlot ? nftsSlot.progressTotal : 3}
              uploadFile={preUploadImage}
              profit={nftsSlot && nftsSlot.art ? nftsSlot.art.profit : 0}
              unoccupied={nftsSlot ? nftsSlot.unoccupied : false}
            />
            <GallerySlot
              slot="invites"
              loading={screenIsLoading}
              title={invitesSlot ? invitesSlot.title : ""}
              description={
                invitesSlot && invitesSlot.description
                  ? invitesSlot.description
                  : ""
              }
              unlocked={invitesSlot ? invitesSlot.unlocked : false}
              level={invitesSlot && invitesSlot.art ? invitesSlot.art.level : 0}
              value={invitesSlot && invitesSlot.art ? invitesSlot.art.value : 0}
              image={
                invitesSlot && invitesSlot.art
                  ? invitesSlot.art.imageAmazonS3URL
                  : ""
              }
              price={invitesSlot ? invitesSlot.unlockSlotPrice : 1000}
              taskTitle={invitesSlot ? invitesSlot.progressTitle : ""}
              current={invitesSlot ? invitesSlot.progressCurrent : 0}
              total={invitesSlot ? invitesSlot.progressTotal : 100}
              uploadFile={preUploadImage}
              profit={
                invitesSlot && invitesSlot.art ? invitesSlot.art.profit : 0
              }
              unoccupied={invitesSlot ? invitesSlot.unoccupied : false}
            />
          </div>

          <div className={classes.flex}>
            <h2>{t.getFree}</h2>
            <div
              className={`${classes.timeLeft} ${
                !timeLeft ? classes.completed : ""
              }`}
            >
              <b>
                <Countdown
                  date={
                    new Date(
                      user.freeARTCtakenDate
                        ? Date.parse(user.freeARTCtakenDate) + 28800000
                        : ""
                    )
                  }
                  daysInHours={true}
                  onTick={(time) => {
                    tickHandler(time.total);
                  }}
                  onComplete={() => {
                    setTimeLeft(0);
                  }}
                />
              </b>{" "}
              {t.left}
            </div>
          </div>
          <div className={classes.getTokens}>
            <div className={classes.bar}>
              <div
                className={classes.barFilled}
                style={{ width: `${(1 - timeLeft / 28800000) * 100}%` }}
              ></div>
            </div>
            <Button
              label={t.get10}
              disabled={timeLeft > 0}
              onClick={getFreeTokensHandler}
            />
          </div>

          <h2>{t.inviteCode}</h2>
          {(!user ||
            user.totalInvitedFriendsCount === undefined ||
            user.totalInvitedFriendsCount === 0) && (
            <p>{invitesSlot?.unlocked ? t.invitePrompt : t.invite5ToUnlock}</p>
          )}
          {user &&
            user.totalInvitedFriendsCount !== undefined &&
            user.totalInvitedFriendsCount > 0 && (
              <p>
                {t.invitedFriends.replace(
                  "{count}",
                  String(user.totalInvitedFriendsCount)
                )}
              </p>
            )}
          <div className={classes.invite}>
            <div className={classes.code}>{user.inviteCode}</div>
            <div
              className={`${classes.copy} ${copied ? classes.copied : ""}`}
              onClick={copyClickHandler}
            >
              {copied ? t.saved : t.copy}
            </div>
          </div>

          <h2>{t.yourNFTs}</h2>
          {screenIsLoading && (
            <div className={classes.loading}>
              <InlineAppLoading />
            </div>
          )}
          {!screenIsLoading && nfts.length > 0 && (
            <div className={classes.nfts}>
              {nfts.map((nft: ArtworkModelDocument) => (
                <NFT
                  key={`nft-${nft._id}`}
                  id={nft._id}
                  img={nft.imageAmazonS3URL}
                />
              ))}
            </div>
          )}
          {!screenIsLoading && nfts.length === 0 && (
            <div className={classes.loading}>
              <p>{t.nftHint}</p>
            </div>
          )}
          {/* <div className={classes.flex}>
            <h2>
              Your Collection{" "}
              <span className={classes.topicsBadge}>{collection.length}</span>
            </h2>
            <div className={classes.timeLeft}>
              <span>Value:&nbsp;</span>
              <b>{collectionWinRate.toFixed(0)}</b>
            </div>
          </div>
          {screenIsLoading && (
            <div className={classes.loading}>
              <InlineAppLoading />
            </div>
          )}
          {!screenIsLoading && collection.length > 0 && (
            <div className={classes.nfts}>
              {collection.map((art: ArtworkModelDocument) => (
                <div
                  className={classes.img}
                  onClick={() => {
                    setPopupImage(art._id);
                  }}
                >
                  <span className={classes.popupIcon}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fillRule="evenodd"
                      clipRule="evenodd"
                      viewBox="0 0 509 511.54"
                    >
                      <path
                        fillRule="nonzero"
                        fill="#fff"
                        d="M447.19 347.03c0-17.06 13.85-30.91 30.91-30.91 17.05 0 30.9 13.85 30.9 30.91v87.82c0 21.08-8.63 40.29-22.51 54.18-13.88 13.88-33.09 22.51-54.18 22.51H76.69c-21.09 0-40.3-8.63-54.18-22.51C8.63 475.14 0 455.93 0 434.85V76.69c0-21.09 8.63-40.3 22.51-54.18C36.39 8.63 55.6 0 76.69 0h86.98c17.06 0 30.9 13.85 30.9 30.9 0 17.06-13.84 30.91-30.9 30.91H76.69c-4.07 0-7.82 1.69-10.51 4.37-2.68 2.69-4.37 6.44-4.37 10.51v358.16c0 4.06 1.69 7.82 4.37 10.5 2.69 2.68 6.44 4.38 10.51 4.38h355.62c4.07 0 7.82-1.7 10.51-4.38 2.68-2.68 4.37-6.44 4.37-10.5v-87.82zm0-243.56L308.15 244.28c-11.91 12.12-31.45 12.28-43.56.37-12.11-11.91-12.28-31.45-.37-43.56L401.77 61.81H309.7c-17.06 0-30.9-13.85-30.9-30.91 0-17.05 13.84-30.9 30.9-30.9h168.4C495.15 0 509 13.85 509 30.9v165.04c0 17.06-13.85 30.9-30.9 30.9-17.06 0-30.91-13.84-30.91-30.9v-92.47z"
                      />
                    </svg>
                  </span>
                  <img
                    src={art.imageAmazonS3ThumbURL || art.imageAmazonS3URL}
                    alt=""
                  />
                  <div className={classes.value}>
                    <span>Value: </span>
                    <b>{computeArtworkScore(art).toFixed(0)}</b>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!screenIsLoading && collection.length === 0 && (
            <div className={classes.loading}>
              <p>Vote to fill in your collection with the best artworks</p>
            </div>
          )} */}
        </div>
      </section>
    </>
  );
};

export default Gallery;
